import { useEffect, useState, useRef } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";

import {
  Users,
  Calendar,
  Clock,
  CheckSquare,
  TrendingUp,
  FileText,
  Bell,
  UserPlus,
  LogIn,
  LogOut,
  DollarSign,
  ChevronRight,
  UserCheck,
  UserX,
  Download,
} from "lucide-react";
import { getDashboardData, getRecentActivities } from "../api/dashboard";
import { tasksApi } from "../api/tasks";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/useToast";

export function Dashboard() {
  const [stats, setStats] = useState<any>(null);
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [displayActivities, setDisplayActivities] = useState<any[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchData = async () => {
    setLoading(true);
    try {
      const todayDate = new Date().toISOString().split("T")[0];

      // Fetch dashboard stats from existing API
      const dashboardResponse = await getDashboardData(todayDate);

      // Fetch open tasks count from tasks API
      let openTasksCount = 0;
      try {
        const tasksResponse = await tasksApi.list({ page_size: 100 });
        const allTasks = tasksResponse.data.items || [];
        openTasksCount = allTasks.filter(t => !['DONE', 'CANCELLED'].includes(t.status)).length;
      } catch (taskError) {
        console.error("Error fetching tasks:", taskError);
      }

      // Extract stats from dashboard response
      if (dashboardResponse) {
        setStats({
          total_employees: dashboardResponse.total_employees,
          activeEmployees: dashboardResponse.activeEmployees,
          pending_leaves: dashboardResponse.pending_leaves,
          todays_present: dashboardResponse.todays_present,
          openTasks: openTasksCount,
          completedTasks: dashboardResponse.completedTasks,
        });
      }

      // Fetch recent activities from new API
      const activitiesResponse = await getRecentActivities(todayDate, 50);

      // Handle different response structures for activities
      let activitiesData = [];
      if (activitiesResponse && activitiesResponse.activities) {
        activitiesData = activitiesResponse.activities;
      } else if (Array.isArray(activitiesResponse)) {
        activitiesData = activitiesResponse;
      } else if (activitiesResponse && activitiesResponse.data) {
        activitiesData = activitiesResponse.data;
      } else {
        activitiesData = [];
      }

      // Process and shuffle activities to get a mixed display
      const processedActivities = processAndShuffleActivities(activitiesData);
      setActivities(processedActivities);

      // Set initial display activities (first 3)
      setDisplayActivities(processedActivities.slice(0, 3));
      setCurrentPageIndex(0);

    } catch (error: any) {
      console.error("Error fetching dashboard data:", error);
      toast({
        // title: "Error",
        description: error.message || "Failed to load dashboard data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Process and SHUFFLE all activities to get a true mix of different types
  const processAndShuffleActivities = (rawActivities: any[]) => {
    const processedActivities: any[] = [];

    for (const activity of rawActivities) {
      const type = activity.type?.toLowerCase();
      const title = (activity.title || "").toLowerCase();
      const description = (activity.description || "").toLowerCase();
      // const employee_name = activity.employee_name || "Employee";
      const employee_name =
  activity.employee_name && activity.employee_name !== "Unknown"
    ? activity.employee_name
    : "Admin";
      const timestamp = activity.timestamp;

      // Handle Attendance (Clock In/Out)
      if (type === "attendance") {
        const status = activity.meta?.status || "";
        if (status === "Clock Out" || title.includes("clocked out")) {
          processedActivities.push({
            ...activity,
            displayType: "clock_out",
            displayMessage: `${employee_name} clocked out`,
            icon: "clock_out",
            color: "red",
            time: timestamp,
            badge: "Checked Out",
            badgeColor: "red"
          });
        } else if (status === "Clock In" || title.includes("clocked in")) {
          processedActivities.push({
            ...activity,
            displayType: "clock_in",
            displayMessage: `${employee_name} clocked in`,
            icon: "clock_in",
            color: "green",
            time: timestamp,
            badge: "Checked In",
            badgeColor: "green"
          });
        } else if (status === "Additional In" || description.includes("additional in")) {
          processedActivities.push({
            ...activity,
            displayType: "clock_in",
            displayMessage: `${employee_name} clocked in (Additional)`,
            icon: "clock_in",
            color: "green",
            time: timestamp,
            badge: "Additional In",
            badgeColor: "green"
          });
        }
      }
      // Handle Leave Activities
      else if (type === "leave") {
        const leaveStatus = activity.meta?.status || "";
        const leaveType = activity.meta?.leave_type || "";
        const days = activity.meta?.total_days || "";

        if (leaveStatus === "APPROVED") {
          processedActivities.push({
            ...activity,
            displayType: "leave_approved",
            displayMessage: `${employee_name}'s ${leaveType} leave approved (${days} days)`,
            icon: "leave",
            color: "green",
            time: timestamp,
            badge: "Leave Approved",
            badgeColor: "green"
          });
        } else if (leaveStatus === "PENDING") {
          processedActivities.push({
            ...activity,
            displayType: "leave_pending",
            displayMessage: `${employee_name} requested ${leaveType} leave`,
            icon: "leave",
            color: "orange",
            time: timestamp,
            badge: "Pending Approval",
            badgeColor: "orange"
          });
        }
      }
      // Handle Salary Activities
      else if (type === "salary") {
        const netSalary = activity.meta?.net_salary || 0;
        const month = activity.meta?.month || "";
        const year = activity.meta?.year || "";

        processedActivities.push({
          ...activity,
          displayType: "salary",
          displayMessage: `Salary processed for ${employee_name}: ₹${netSalary} for ${month} ${year}`,
          icon: "salary",
          color: "purple",
          time: timestamp,
          badge: "Salary Processed",
          badgeColor: "purple"
        });
      }

      else if (type === "task") {
        const taskStatus = activity.meta?.status || "";
        const taskTitle = activity.title || "Task"; 

        if (taskStatus === "DONE") {
          processedActivities.push({
            ...activity,
            displayType: "task_completed",
            displayMessage: `${employee_name} completed task: ${taskTitle}`,
            icon: "check_square",
            color: "green",
            time: activity.timestamp,
            badge: "Task Completed",
            badgeColor: "green"
          });
        }
        else if (taskStatus === "OPEN" || taskStatus === "TODO") {  
          processedActivities.push({
            ...activity,
            displayType: "task_open",
            displayMessage: `${employee_name} has a task: ${taskTitle}`,
            icon: "check_square",
            color: "orange",
            time: activity.timestamp,
            badge: "Open Task",
            badgeColor: "orange"
          });
        }
      }
    }

    // Sort by time (most recent first)
    processedActivities.sort((a, b) => {
      const timeA = a.time ? new Date(a.time).getTime() : 0;
      const timeB = b.time ? new Date(b.time).getTime() : 0;
      return timeB - timeA;
    });

    // Shuffle the activities to ensure a true mix (but keep recent ones first)
    const shuffled = shuffleArrayWithPriority(processedActivities);

    return shuffled;
  };

  // Shuffle array but keep recent items with higher priority
  const shuffleArrayWithPriority = (arr: any[]) => {
    if (arr.length <= 3) return arr;

    // Take first 8 most recent items and shuffle them
    const recentItems = arr.slice(0, Math.min(9, arr.length));
    const olderItems = arr.slice(Math.min(9, arr.length));

    // Fisher-Yates shuffle for recent items
    for (let i = recentItems.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [recentItems[i], recentItems[j]] = [recentItems[j], recentItems[i]];
    }

    return [...recentItems, ...olderItems];
  };

  // Auto-rotate activities every 30 seconds - showing different batches
  useEffect(() => {
    if (activities.length <= 3) return;

    const interval = setInterval(() => {
      setCurrentPageIndex((prev) => {
        const totalPages = Math.ceil(activities.length / 3);
        const nextPage = (prev + 1) % totalPages;
        const startIdx = nextPage * 3;
        const newDisplay = activities.slice(startIdx, startIdx + 3);

        // If we don't have enough items for a full page, show from beginning
        if (newDisplay.length < 3 && nextPage !== 0) {
          const fallbackDisplay = activities.slice(0, 3);
          setDisplayActivities(fallbackDisplay);
          return 0;
        }

        setDisplayActivities(newDisplay);
        return nextPage;
      });
    }, 30000);

    return () => clearInterval(interval);
  }, [activities]);

  useEffect(() => {
    fetchData();
  }, []);

  // Refresh data every 30 seconds
  // useEffect(() => {
  //   const refreshInterval = setInterval(() => {
  //     fetchData();
  //   }, 30000);

  //   return () => clearInterval(refreshInterval);
  // }, []);

  if (loading && activities.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const getWelcomeMessage = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  const getStatsForRole = () => {
    const baseStats = [
      {
        title: "Total Employees",
        value: stats?.total_employees || 0,
        icon: Users,
        tone: "info" as const,
        description: `${stats?.activeEmployees || 0} active`,
      },
      {
        title: "Pending Leaves",
        value: stats?.pending_leaves || 0,
        icon: Calendar,
        tone: "warning" as const,
        description: "Requires approval",
      },
      {
        title: "Today's Attendance",
        value: `${stats?.todays_present || 0}`,
        icon: Clock,
        tone: "success" as const,
        description: "Present today",
      },
      {
        title: "Active Tasks",
        value: stats?.openTasks || 0,
        icon: CheckSquare,
        tone: "primary" as const,
        description: `${stats?.completedTasks || 0} completed`,
      },
    ];
    if (user?.role === "employee") {
      return baseStats.slice(1, 4);
    }
    return baseStats;
  };

  // Tone palette for premium stat tiles (matches Task Reports aesthetic)
  const STAT_TONES: Record<string, {
    iconBg: string;
    iconFg: string;
    valueColor?: string;
  }> = {
    info:    { iconBg: 'var(--gt-prio-low-bg)',  iconFg: 'var(--gt-prio-low-fg)' },
    warning: { iconBg: 'var(--gt-prio-med-bg)',  iconFg: 'var(--gt-prio-med-fg)' },
    success: { iconBg: 'var(--gt-status-done-bg)', iconFg: 'var(--gt-status-done-fg)', valueColor: 'var(--gt-accent-strong)' },
    danger:  { iconBg: 'var(--gt-prio-urgent-bg)', iconFg: 'var(--gt-prio-urgent-fg)', valueColor: '#7f1d1d' },
    primary: { iconBg: 'var(--gt-primary-container)', iconFg: 'var(--gt-primary-deep)' },
  };

  const getActivityIconComponent = (iconType: string) => {
    switch (iconType) {
      case "clock_in":
        return <LogIn className="h-4 w-4" />;
      case "clock_out":
        return <LogOut className="h-4 w-4" />;
      case "leave":
        return <Calendar className="h-4 w-4" />;
      case "employee":
        return <UserPlus className="h-4 w-4" />;
      case "salary":
        return <DollarSign className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getActivityColorClass = (color: string) => {
    switch (color) {
      case "green":
        return "bg-green-100 text-green-600";
      case "red":
        return "bg-red-100 text-red-600";
      case "orange":
        return "bg-orange-100 text-orange-600";
      case "blue":
        return "bg-blue-100 text-blue-600";
      case "purple":
        return "bg-purple-100 text-purple-600";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const getBadgeColorClass = (badgeColor: string) => {
    switch (badgeColor) {
      case "green":
        return "bg-green-50 text-green-600 border-green-200";
      case "red":
        return "bg-red-50 text-red-600 border-red-200";
      case "orange":
        return "bg-orange-50 text-orange-600 border-orange-200";
      case "purple":
        return "bg-purple-50 text-purple-600 border-purple-200";
      default:
        return "bg-gray-50 text-gray-600 border-gray-200";
    }
  };

  const getTimeAgo = (timestamp: string) => {
    if (!timestamp) return "Just now";
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return "Recently";
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins} min ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      return date.toLocaleDateString();
    } catch {
      return "Recently";
    }
  };

  const currentDisplay = displayActivities;

  return (
    <div className="gt-shell min-h-screen">
      <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-6 gt-fade-in">
        {/* Welcome Section */}
        <div className="gt-page-hero flex-col md:flex-row gap-4">
          <div>
            <div className="gt-eyebrow-row">
              <span className="gt-leaf" />
              <span className="gt-eyebrow">i. Workspace</span>
            </div>
            <h1 className="gt-page-title">
              {getWelcomeMessage()},{" "}
              <em>{user?.name?.split(" ")[0] || "Admin"}</em>
            </h1>
            <p className="gt-page-sub" style={{ marginTop: 6 }}>
              Here's what's happening with your{" "}
              <em className="gt-serif-italic">
                {user?.role === "admin" ? "organization" : "work"}
              </em>{" "}
              today.
            </p>
          </div>

          <div className="hidden md:block">
            <div
              className="gt-card"
              style={{ padding: '14px 18px', minWidth: 220 }}
            >
              <div
                className="gt-eyebrow"
                style={{ marginBottom: 6 }}
              >
                Today's Date
              </div>
              <div
                className="gt-display"
                style={{
                  fontSize: 18,
                  color: 'var(--gt-text-primary)',
                  letterSpacing: '-0.01em',
                }}
              >
                {new Date().toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid — premium pattern with icon chips */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {getStatsForRole().map((stat, index) => {
            const tone = STAT_TONES[stat.tone] || STAT_TONES.primary;
            return (
              <div
                key={index}
                className="gt-card"
                style={{
                  padding: '20px 22px',
                  position: 'relative',
                  overflow: 'hidden',
                  transition:
                    'transform 200ms cubic-bezier(0.33, 1, 0.68, 1), box-shadow 200ms cubic-bezier(0.33, 1, 0.68, 1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = 'var(--gt-elev-3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = 'var(--gt-elev-1)';
                }}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <span
                    className="gt-eyebrow"
                    style={{ marginBottom: 0, fontSize: 10.5 }}
                  >
                    {stat.title}
                  </span>
                  <span
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 9,
                      background: tone.iconBg,
                      color: tone.iconFg,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5)',
                    }}
                  >
                    <stat.icon className="w-4 h-4" />
                  </span>
                </div>
                <p
                  className="gt-display gt-numerals"
                  style={{
                    fontSize: 32,
                    fontWeight: 800,
                    letterSpacing: '-0.03em',
                    color: tone.valueColor || 'var(--gt-text-primary)',
                    lineHeight: 1,
                    marginTop: 4,
                  }}
                >
                  {stat.value}
                </p>
                <p
                  className="gt-page-sub gt-serif-italic"
                  style={{ fontSize: 11.5, marginTop: 8 }}
                >
                  {stat.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Performance Health — donut + breakdown */}
        {(() => {
          const totalEmp = stats?.total_employees || 0;
          const presentToday = stats?.todays_present || 0;
          const attendancePct = totalEmp > 0
            ? Math.round((presentToday / totalEmp) * 100)
            : 0;
          const openTasks = stats?.openTasks || 0;
          const completedTasks = stats?.completedTasks || 0;
          const totalTasks = openTasks + completedTasks;
          const taskCompletionPct = totalTasks > 0
            ? Math.round((completedTasks / totalTasks) * 100)
            : 0;
          const donutR = 56;
          const donutC = 2 * Math.PI * donutR;

          // Breakdown rows for status visualisation
          const breakdown = [
            {
              label: 'Present Today',
              count: presentToday,
              total: totalEmp || 1,
              color: 'linear-gradient(90deg, #5eead4 0%, #14b890 100%)',
            },
            {
              label: 'Pending Leaves',
              count: stats?.pending_leaves || 0,
              total: Math.max(totalEmp || 1, 1),
              color: 'linear-gradient(90deg, #fcd34d 0%, #f59e0b 100%)',
            },
            {
              label: 'Active Tasks',
              count: openTasks,
              total: totalTasks || 1,
              color: 'linear-gradient(90deg, #93c5fd 0%, #3b82f6 100%)',
            },
          ];

          return (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Big completion donut */}
              <div
                className="gt-card lg:col-span-1"
                style={{ padding: 22, position: 'relative' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="gt-leaf" />
                  <span className="gt-eyebrow">i. Today</span>
                </div>
                <h3
                  className="gt-display"
                  style={{
                    fontSize: 16,
                    color: 'var(--gt-text-primary)',
                    marginBottom: 14,
                  }}
                >
                  Attendance{' '}
                  <em
                    className="gt-serif-italic"
                    style={{ color: 'var(--gt-accent-strong)' }}
                  >
                    Health
                  </em>
                </h3>

                <div
                  className="flex items-center justify-center"
                  style={{ padding: '12px 0' }}
                >
                  <div style={{ position: 'relative', width: 160, height: 160 }}>
                    <svg
                      width={160}
                      height={160}
                      viewBox="0 0 160 160"
                      style={{ transform: 'rotate(-90deg)' }}
                    >
                      <defs>
                        <linearGradient
                          id="gt-dashboard-ring"
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="100%"
                        >
                          <stop offset="0%" stopColor="#5eead4" />
                          <stop offset="50%" stopColor="#14b890" />
                          <stop offset="100%" stopColor="#047857" />
                        </linearGradient>
                      </defs>
                      <circle
                        cx={80}
                        cy={80}
                        r={donutR}
                        fill="none"
                        stroke="var(--gt-surface-2)"
                        strokeWidth={12}
                      />
                      <circle
                        cx={80}
                        cy={80}
                        r={donutR}
                        fill="none"
                        stroke="url(#gt-dashboard-ring)"
                        strokeWidth={12}
                        strokeLinecap="round"
                        strokeDasharray={donutC}
                        strokeDashoffset={donutC * (1 - attendancePct / 100)}
                        style={{
                          transition:
                            'stroke-dashoffset 800ms cubic-bezier(0.4, 0, 0.2, 1)',
                          filter:
                            'drop-shadow(0 4px 12px rgba(20, 184, 144, 0.35))',
                        }}
                      />
                    </svg>
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <span
                        className="gt-display gt-numerals"
                        style={{
                          fontSize: 36,
                          fontWeight: 800,
                          letterSpacing: '-0.03em',
                          color: 'var(--gt-text-primary)',
                          lineHeight: 1,
                        }}
                      >
                        {attendancePct}
                        <span
                          style={{
                            fontSize: 18,
                            color: 'var(--gt-text-tertiary)',
                          }}
                        >
                          %
                        </span>
                      </span>
                      <span
                        className="gt-eyebrow"
                        style={{ marginTop: 4, marginBottom: 0 }}
                      >
                        present
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  className="flex justify-between gt-page-sub gt-numerals"
                  style={{ marginTop: 12, fontSize: 11.5 }}
                >
                  <span>
                    <span style={{ color: 'var(--gt-accent-strong)' }}>●</span>{' '}
                    {presentToday} present
                  </span>
                  <span style={{ color: 'var(--gt-text-tertiary)' }}>
                    <span style={{ color: '#94a3b8' }}>●</span>{' '}
                    {Math.max(totalEmp - presentToday, 0)} away
                  </span>
                </div>
              </div>

              {/* Breakdown card */}
              <div
                className="gt-card lg:col-span-2"
                style={{ padding: 26 }}
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <TrendingUp
                      className="w-4 h-4"
                      style={{ color: 'var(--gt-accent-strong)' }}
                    />
                    <h3
                      className="gt-display"
                      style={{
                        fontSize: 15,
                        color: 'var(--gt-text-primary)',
                      }}
                    >
                      Organisation <em className="gt-serif-italic">Pulse</em>
                    </h3>
                  </div>
                  <span className="gt-eyebrow" style={{ marginBottom: 0 }}>
                    live
                  </span>
                </div>
                <div className="space-y-4">
                  {breakdown.map((row) => {
                    const pct = Math.min(
                      100,
                      Math.round((row.count / row.total) * 100),
                    );
                    return (
                      <div key={row.label}>
                        <div
                          className="flex justify-between gt-numerals"
                          style={{ fontSize: 12.5, marginBottom: 6 }}
                        >
                          <span
                            className="gt-display"
                            style={{
                              fontWeight: 600,
                              color: 'var(--gt-text-secondary)',
                            }}
                          >
                            {row.label}
                          </span>
                          <span style={{ color: 'var(--gt-text-tertiary)' }}>
                            <span
                              className="gt-display"
                              style={{ color: 'var(--gt-text-primary)' }}
                            >
                              {row.count}
                            </span>{' '}
                            · {pct}%
                          </span>
                        </div>
                        <div
                          style={{
                            height: 8,
                            background: 'var(--gt-surface-2)',
                            borderRadius: 999,
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              height: '100%',
                              borderRadius: 999,
                              background: row.color,
                              width: `${pct}%`,
                              boxShadow:
                                'inset 0 1px 0 rgba(255,255,255,0.45)',
                              transition:
                                'width 600ms cubic-bezier(0.4,0,0.2,1)',
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div
                  className="grid grid-cols-2 gap-3"
                  style={{
                    marginTop: 22,
                    paddingTop: 18,
                    borderTop: '1px solid var(--gt-divider)',
                  }}
                >
                  <div>
                    <span
                      className="gt-eyebrow"
                      style={{ marginBottom: 4, display: 'block' }}
                    >
                      Task completion
                    </span>
                    <span
                      className="gt-display gt-numerals"
                      style={{
                        fontSize: 22,
                        fontWeight: 800,
                        letterSpacing: '-0.025em',
                        color: 'var(--gt-text-primary)',
                      }}
                    >
                      {taskCompletionPct}%
                    </span>
                    <span
                      className="gt-page-sub gt-serif-italic"
                      style={{ fontSize: 11.5, marginLeft: 6 }}
                    >
                      finished
                    </span>
                  </div>
                  <div>
                    <span
                      className="gt-eyebrow"
                      style={{ marginBottom: 4, display: 'block' }}
                    >
                      Active workforce
                    </span>
                    <span
                      className="gt-display gt-numerals"
                      style={{
                        fontSize: 22,
                        fontWeight: 800,
                        letterSpacing: '-0.025em',
                        color: 'var(--gt-text-primary)',
                      }}
                    >
                      {stats?.activeEmployees || 0}
                    </span>
                    <span
                      className="gt-page-sub gt-serif-italic"
                      style={{ fontSize: 11.5, marginLeft: 6 }}
                    >
                      of {totalEmp}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              Quick Actions
            </CardTitle>
            <CardDescription>Common tasks and shortcuts</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {user?.role === "employee" ? (
              <>
                <Link to="/attendance">
                  <Button className="w-full justify-start gap-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700">
                    <Clock className="h-4 w-4" />
                    Clock In/Out
                  </Button>
                </Link>

                <Link to="/leaves">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2 hover:bg-blue-50"
                  >
                    <Calendar className="h-4 w-4" />
                    Apply for Leave
                  </Button>
                </Link>

                <Link to="/tasks/my-tasks">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2 hover:bg-purple-50"
                  >
                    <CheckSquare className="h-4 w-4" />
                    View My Tasks
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/employees" className="w-full">
                  <Button className="w-full justify-start gap-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
                    <Users className="h-4 w-4" />
                    Add Employee
                  </Button>
                </Link>

                <Link to="/leaves" className="w-full">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2 hover:bg-orange-50"
                  >
                    <Calendar className="h-4 w-4" />
                    Approve Leaves
                  </Button>
                </Link>

                <Link to="/reports" className="w-full">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2 hover:bg-green-50"
                  >
                    <FileText className="h-4 w-4" />
                    Generate Reports
                  </Button>
                </Link>

                <Link to="/downloads" className="w-full">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2 hover:bg-orange-50"
                  >
                    <Download className="h-4 w-4" />
                    Downloads
                  </Button>
                </Link>
              </>
            )}
          </CardContent>
        </Card>

        {/* Recent Activities - Mixed Activity Types with Auto-rotation */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
                  <Bell className="h-5 w-5 text-orange-600" />
                  Today's Activities
                </CardTitle>
                <CardDescription>
                  Attendance • Leaves • Salary
                </CardDescription>
              </div>
              {activities.length > 4 && (
                <div className="flex gap-1.5">
                  {Array.from({ length: Math.min(5, Math.ceil(activities.length / 4)) }).map((_, idx) => (
                    <div
                      key={idx}
                      className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${currentPageIndex === idx
                        ? "w-5 bg-orange-500"
                        : "w-1.5 bg-gray-300 hover:bg-gray-400"
                        }`}
                      onClick={() => {
                        const startIdx = idx * 4;
                        setDisplayActivities(activities.slice(startIdx, startIdx + 4));
                        setCurrentPageIndex(idx);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {currentDisplay.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bell className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                <p className="text-sm">No recent activities</p>
                <p className="text-xs mt-1">Updates will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {currentDisplay.map((activity, idx) => (
                  <div
                    key={activity.id || `${activity.displayType}-${idx}-${activity.time}`}
                    className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all duration-300 group animate-slide-in"
                    style={{ animationDelay: `${idx * 0.05}s` }}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-full flex-shrink-0 ${getActivityColorClass(activity.color)}`}>
                        {getActivityIconComponent(activity.icon)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {activity.displayMessage}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-xs text-gray-400 flex-shrink-0">
                            {getTimeAgo(activity.time || activity.timestamp)}
                          </span>
                          {activity.badge && (
                            <Badge variant="outline" className={`text-xs ${getBadgeColorClass(activity.badgeColor)}`}>
                              {activity.badge}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                ))}
              </div>
            )}

            {/* Auto-rotate indicator */}
            {activities.length > 4 && (
              <div className="mt-4 pt-3 border-t text-center">
                <p className="text-xs text-gray-400">
                  🔄 Activities rotate every 5 seconds • {Math.ceil(activities.length / 4)} pages
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

        {/* Animation styles */}
        <style>{`
          @keyframes slide-in {
            from {
              opacity: 0;
              transform: translateX(-10px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

          .animate-slide-in {
            animation: slide-in 0.3s ease-out forwards;
            opacity: 0;
          }
        `}</style>
      </div>
    </div>
  );
}